/**
 * OrderFlagsButton: the Compact / Narrow Order Queue flags cell.
 *
 * A 28px flag button with the number of the row's visible flags. Hovering it
 * with a mouse, focusing it from the keyboard, or tapping it shows the same
 * pills, in the same order and style, as the wide Flags column. A mouse click
 * pins it open (it stays after the pointer leaves) until a second click, an
 * outside click or Escape, as in the approved mockup. Taps and Enter / Space
 * toggle it. Clicking never expands the row.
 *
 * Popover, not Tooltip or HoverCard: of the three, only Popover handles touch
 * and Escape natively. It is controlled (useRowPopover) so hover and keyboard
 * focus can open it too, and its content mounts only while open.
 */
import { useId, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Flag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useRowPopover } from './useRowPopover';

export interface OrderFlagsButtonProps {
  /** Displayed order number (no leading #), for the label and the heading. */
  orderLabel: string;
  /** Visible flags, TEST excluded; the caller renders nothing when 0. */
  count: number;
  /** The flag names, comma-separated, for screen readers while closed. */
  description?: string;
  /** The SAME pill list the wide Flags cell renders. */
  children: ReactNode;
}

export function OrderFlagsButton({ orderLabel, count, description, children }: OrderFlagsButtonProps) {
  const { t } = useTranslation('production');
  const { rootProps, triggerProps, contentProps } = useRowPopover();
  const descriptionId = useId();
  const headingId = useId();

  return (
    <Popover {...rootProps}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-7 w-7 shrink-0 gap-0.5 [&_svg]:size-3.5 data-[state=open]:bg-accent data-[state=open]:text-accent-foreground"
          aria-label={t('orders.flagsButton.label', { name: orderLabel, count })}
          aria-describedby={description ? descriptionId : undefined}
          {...triggerProps}
        >
          <Flag aria-hidden="true" />
          <span aria-hidden="true" className="text-[11px] font-semibold leading-none tabular-nums">
            {count}
          </span>
        </Button>
      </PopoverTrigger>
      {description ? (
        <span id={descriptionId} className="sr-only">
          {description}
        </span>
      ) : null}
      <PopoverContent
        {...contentProps}
        align="end"
        aria-labelledby={headingId}
        // `!`: the primitive's data-[state=open]:animate-in outranks a plain
        // motion-reduce:animate-none (attribute selector, higher specificity).
        className="w-auto max-w-xs p-2 motion-reduce:!animate-none"
      >
        <p id={headingId} className="mb-1.5 text-xs font-medium text-muted-foreground">
          {t('orders.flagsButton.heading', { name: orderLabel })}
        </p>
        <div className="flex flex-wrap gap-1">{children}</div>
      </PopoverContent>
    </Popover>
  );
}
